import React from 'react';
import { toast } from 'react-toastify';
import { X, Copy, Share2, MessageCircle, Send, Facebook, Instagram } from 'lucide-react';

/**
 * Share sheet used for both products (Change 15) and shops (Change 9).
 *
 * `url` must be absolute — WhatsApp, Telegram and Facebook all need a
 * fully-qualified URL, and a relative one silently produces a dead share.
 */
const copyToClipboard = async (url) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      // Older mobile browsers, and any page not served over HTTPS, have no
      // clipboard API.
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    return true;
  } catch (error) {
    console.error('Clipboard write failed:', error);
    return false;
  }
};

const ShareSheet = ({ isOpen, onClose, url, title, text }) => {
  if (!isOpen) return null;

  const shareUrl = url || window.location.href;
  const shareText = text || title || 'Check this out on Zammer!';

  const openShareWindow = (target) => {
    window.open(target, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(shareUrl);
    toast[ok ? 'success' : 'error'](ok ? 'Link copied to clipboard!' : 'Could not copy the link');
    onClose();
  };

  // Instagram has no public web intent for sharing an arbitrary link, so the
  // honest fallback is to copy it for the user to paste into a story or DM.
  // On mobile the native sheet below lists Instagram directly.
  const handleInstagram = async () => {
    const ok = await copyToClipboard(shareUrl);
    toast[ok ? 'info' : 'error'](
      ok ? 'Link copied — paste it into your Instagram story or DM' : 'Could not copy the link'
    );
    onClose();
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      toast.info('Your browser does not support the share menu — copying the link instead');
      await handleCopy();
      return;
    }
    try {
      await navigator.share({ title, text: shareText, url: shareUrl });
      onClose();
    } catch (error) {
      // The user dismissing the sheet rejects the promise; that is not an error.
      if (error.name !== 'AbortError') {
        console.error('Native share failed:', error);
        toast.error('Could not open the share menu');
      }
    }
  };

  const options = [
    {
      key: 'copy',
      label: 'Copy Link',
      icon: Copy,
      className: 'bg-gray-100 text-gray-700',
      onClick: handleCopy
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      className: 'bg-green-100 text-green-600',
      onClick: () =>
        openShareWindow(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`)
    },
    {
      key: 'instagram',
      label: 'Instagram',
      icon: Instagram,
      className: 'bg-pink-100 text-pink-600',
      onClick: handleInstagram
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      className: 'bg-blue-100 text-blue-600',
      onClick: () =>
        openShareWindow(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
        )
    },
    {
      key: 'telegram',
      label: 'Telegram',
      icon: Send,
      className: 'bg-sky-100 text-sky-600',
      onClick: () =>
        openShareWindow(
          `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
        )
    },
    {
      key: 'more',
      label: 'More apps',
      icon: Share2,
      className: 'bg-orange-100 text-orange-600',
      onClick: handleNativeShare
    }
  ];

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-end justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-[480px] rounded-t-2xl p-5 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold text-gray-900">Share</h3>
          <button
            onClick={onClose}
            aria-label="Close share menu"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {options.map(({ key, label, icon: Icon, className, onClick }) => (
            <button
              key={key}
              onClick={onClick}
              className="flex flex-col items-center gap-2 p-2 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <span className={`w-12 h-12 rounded-full flex items-center justify-center ${className}`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="text-xs font-medium text-gray-700 text-center leading-tight">
                {label}
              </span>
            </button>
          ))}
        </div>

        <p className="mt-5 text-[11px] text-gray-400 text-center break-all">{shareUrl}</p>
      </div>
    </div>
  );
};

export default ShareSheet;
